import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function CacheClearButton() {
  const [isClearing, setIsClearing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { toast } = useToast();

  const clearCache = async () => {
    setIsClearing(true);
    setShowConfirm(false);
    
    try {
      // Cache in React Query leeren
      await queryClient.invalidateQueries();
      
      // Browser-Cache für aktuelle Seite leeren
      if ('caches' in window) {
        const cachesAvailable = await caches.keys();
        await Promise.all(
          cachesAvailable.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
      }
      
      // Seite neu laden ohne Cache
      location.reload();
      
      toast({
        title: "Cache geleert",
        description: "Der Seiten-Cache wurde erfolgreich geleert und die Seite wird neu geladen."
      });
    } catch (error) {
      toast({
        title: "Fehler beim Leeren des Caches",
        description: error instanceof Error ? error.message : "Ein unbekannter Fehler ist aufgetreten.",
        variant: "destructive"
      });
      setIsClearing(false);
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => setShowConfirm(true)}
        disabled={isClearing}
        className="gap-2 bg-white/10 hover:bg-white/20 border-white/20 text-white whitespace-nowrap"
      >
        <RefreshCw className={`h-4 w-4 ${isClearing ? 'animate-spin' : ''}`} />
        <span className="text-nowrap">
          {isClearing ? "Cache wird geleert..." : "Cache leeren"}
        </span>
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cache leeren</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie wirklich den Cache leeren? Die Seite wird neu geladen und alle nicht gespeicherten Änderungen gehen verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={clearCache}>Cache leeren</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 